export function formatSelectOption(value) {
    return {
        id: value.id,
        text: value.name
    };
}

export function formatCurrencyWithoutSymbolToNumber(value) {
    if (!value) return 0;

    const replacedValue = value.replace(/[^\d]/g, "");

    const onlyDigits = replacedValue
        .split("")
        .filter((s) => /\d/.test(s))
        .join("")
        .padStart(5, "0");

    if (isNaN(onlyDigits)) {
        return;
    }

    const digitsFloat = onlyDigits.slice(0, -4) + "." + onlyDigits.slice(-4);

    return Number(digitsFloat);
}

export function formatCurrencyWithoutSymbol(value) {
    if (value === null || value === undefined) return "";

    const numberValue =
        typeof value === "string" ? formatCurrencyWithoutSymbolToNumber(value) : value;

    let formattedValue = new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
    }).format(numberValue);

    return formattedValue;
}

/**
 * Parses a date string into a JavaScript Date object, supporting multiple formats and time zones.
 *
 * @param {string} dateTimeStr - The date string to be parsed (e.g., "20/02/2025", "02-20-2025 14:30").
 * @param {"DMY" | "MDY" | "YMD" | "YDM"} format - The expected date format:
 *   - "DMY" (Day/Month/Year) → "20/02/2025"
 *   - "MDY" (Month/Day/Year) → "02/20/2025"
 *   - "YMD" (Year/Month/Day) → "2025-02-20"
 *   - "YDM" (Year/Day/Month) → "2025-20-02" (default: "YMD")
 * @param {string} [timeZone="UTC"] - The target time zone (default: "UTC").
 *   Examples: "America/New_York", "Asia/Tokyo", "Europe/London".
 * @returns {Date | null} A Date object in the specified time zone, or null if an error occurs.
 */
export function parseDate(dateTimeStr, format = "YMD", timeZone = "UTC") {
    try {
        if (!dateTimeStr || typeof dateTimeStr !== "string") {
            throw new Error("Invalid input: Date must be a non-empty string.");
        }

        if (!["DMY", "MDY", "YMD", "YDM"].includes(format)) {
            throw new Error(
                "Invalid format: Use 'DMY' (DD/MM/YYYY), 'MDY' (MM/DD/YYYY), 'YMD' (YYYY/MM/DD), or 'YDM' (YYYY/DD/MM)."
            );
        }

        // Normalize separators to "/"
        let [datePart, timePart] = dateTimeStr.trim().split(" ");
        datePart = datePart.replace(/[-.]/g, "/");
        const dateParts = datePart.split("/").map(Number);

        if (dateParts.length !== 3) {
            throw new Error(
                "Invalid date format: Ensure it's DD/MM/YYYY, MM/DD/YYYY, YYYY/MM/DD or YYYY/DD/MM."
            );
        }

        let year, month, day;

        // Assign year, month, day based on format
        if (format === "DMY") {
            [day, month, year] = dateParts;
        } else if (format === "MDY") {
            [month, day, year] = dateParts;
        } else if (format === "YMD") {
            [year, month, day] = dateParts;
        } else if (format === "YDM") {
            [year, day, month] = dateParts;
        }

        if (month < 1 || month > 12 || day < 1 || day > 31) {
            throw new Error("Invalid date: Ensure values are correct.");
        }

        // Handle optional time (default to "00:00:00" if missing)
        let hours = 0,
            minutes = 0,
            seconds = 0;
        if (timePart) {
            const timeParts = timePart.split(":").map(Number);
            [hours, minutes, seconds] = timeParts;
            if (isNaN(seconds)) seconds = 0; // Handle "HH:mm" format
        }

        // Create UTC Date Object (avoids time zone offset issues)
        const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));

        // Convert to the selected time zone
        const dateInTimeZone = new Intl.DateTimeFormat("en-US", {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
        }).formatToParts(utcDate);

        // Extract values from the formatted output
        const getPart = (type) => dateInTimeZone.find((p) => p.type === type)?.value;
        const formattedDate = new Date(
            `${getPart("year")}-${getPart("month")}-${getPart("day")}T${getPart("hour")}:${getPart("minute")}:${getPart("second")}`
        );

        if (isNaN(formattedDate.getTime())) {
            throw new Error("Invalid date object: Ensure the input is correct.");
        }

        return formattedDate;
    } catch (error) {
        console.error("Error parsing date:", error.message);
        return null;
    }
}

export function adjustTimeZone3(date) {
    if (!date) return null;

    if (!(date instanceof Date)) {
        date = new Date(date);
    }
    const offset = date.getTimezoneOffset() * 60000;
    const localISOTime = new Date(date.getTime() - offset).toISOString().slice(0, -1);

    return localISOTime;
}
